import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

// A "⋯" button with a short list of secondary actions. Closes on pick, outside click or Escape.
// items: [{ label, icon: Icon, onClick, disabled, danger }]; falsy items are skipped.
export default function OverflowMenu({ label, items, className = '' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const list = (items || []).filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!list.length) return null;
  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-moss hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest dark:text-ink-muted-dark dark:hover:bg-moss-dark dark:hover:text-ink-dark"
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>
      {open && (
        <ul role="menu" className="absolute end-0 top-full z-30 mt-1 min-w-[13rem] overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg dark:border-line-dark dark:bg-surface-dark">
          {list.map(({ label: itemLabel, icon: Icon, onClick, disabled, danger }) => (
            <li key={itemLabel} role="none">
              <button
                type="button"
                role="menuitem"
                disabled={disabled}
                onClick={() => { setOpen(false); onClick(); }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-start text-[13px] font-medium hover:bg-moss focus-visible:bg-moss focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-moss-dark dark:focus-visible:bg-moss-dark ${danger ? 'text-red-700 dark:text-red-400' : 'text-ink dark:text-ink-dark'}`}
              >
                {Icon && <Icon size={15} aria-hidden="true" className="shrink-0 opacity-80" />}
                {itemLabel}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
